import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { Shell } from '../components/Shell'
import { Field, Sill, Check } from '../components/ui'

/**
 * Report an issue / Suggest a feature.
 *
 * One screen, two words, because the form is identical and only the framing
 * differs — splitting it into two components would mean fixing every bug here
 * twice.
 *
 * The scope toggle only appears on a suggestion, and only for someone who is
 * actually in a church. A bug is a bug regardless of who is looking at it;
 * "add a Portuguese hymn book" is a different request when it comes from one
 * congregation than when it comes from everyone, and the two should not land
 * in the same pile. RLS enforces the same rule server-side: a church-scoped
 * row has to name a church you are an approved member of.
 */

const COPY = {
  issue: {
    eyebrow: 'Support',
    title: 'Something broken?',
    sub: 'Tell us what happened. The more ordinary the detail, the easier it is to find.',
    subjectLabel: 'What went wrong',
    subjectHint: 'The chord chart prints without the last verse',
    bodyLabel: 'What were you doing when it happened?',
    bodyHint: 'I opened a song, tapped Chart, then Print…',
    send: 'Send the report',
    done: 'Reported.',
    doneSub: 'It is on the list. If we need more from you, we will email you.',
  },
  feature: {
    eyebrow: 'Support',
    title: 'Something missing?',
    sub: 'Tell us what you wanted to do and could not.',
    subjectLabel: 'What would you add',
    subjectHint: 'Let two writers edit the same sheet at once',
    bodyLabel: 'What would it let you do?',
    bodyHint: 'We write in the same room on a Tuesday and…',
    send: 'Send the suggestion',
    done: 'Suggested.',
    doneSub: 'Read by a person, not a form. Thank you for taking the time.',
  },
}

const ago = (iso) => {
  if (!iso) return null
  const d = Math.floor((Date.now() - new Date(iso)) / 8.64e7)
  if (d < 1) return 'today'
  if (d === 1) return 'yesterday'
  return `${d} days ago`
}

export default function Feedback() {
  const { kind = 'issue' } = useParams()
  const { user, church } = useSession()
  const nav = useNavigate()

  const copy = COPY[kind] ?? COPY.issue
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [scope, setScope] = useState('global')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [err, setErr] = useState(null)

  // Only a suggestion can be church-scoped, and only if there is a church to
  // scope it to.
  const canScope = kind === 'feature' && !!church

  // The button used to disable itself on an empty field, which reads as a dead
  // page: nothing happens, nothing says why. It stays live and tells you.
  const [touched, setTouched] = useState(false)
  const missing = !subject.trim() ? 'subject' : !body.trim() ? 'body' : null

  // What they have already sent, and anything we sent back.
  //
  // Until this existed, a support message was a one-way door: the form said
  // "we will email you" and email was the only place an answer could ever
  // appear. That is fine right up until the address changes, the mail is
  // filtered, or they simply want to check whether they imagined the whole
  // exchange. "read own feedback" (user_id = auth.uid()) has permitted this
  // since migration 30 — nothing needed opening up, it just had no screen.
  const [mine, setMine] = useState(null)
  const loadMine = useCallback(async () => {
    if (!user?.id) return
    const { data } = await supabase.from('feedback')
      .select('id, kind, subject, body, status, created_at, reply_body, replied_at')
      // Filtered explicitly, unlike the staff queue where RLS narrowing to the
      // caller is the whole point. "read own feedback" is not the only policy
      // on this table: a superadmin also holds "superadmin reads all feedback",
      // so relying on RLS here would put every writer's support messages under
      // the heading "what you have sent us" on staff's own support screen.
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20)
    setMine(data ?? [])
  }, [user?.id])
  useEffect(() => { loadMine() }, [loadMine])

  const send = async () => {
    if (missing) return setTouched(true)
    setBusy(true); setErr(null)
    const churchScoped = canScope && scope === 'church'
    const row = {
      user_id: user.id,
      kind,
      scope: churchScoped ? 'church' : 'global',
      church_id: churchScoped ? church.id : null,
      subject: subject.trim(),
      body: body.trim(),
      // Captured rather than asked for. Nobody can recite their browser
      // version, and every one of these questions is one more reason to
      // abandon the form.
      app_version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null,
      user_agent: navigator.userAgent.slice(0, 500),
      path: window.location.pathname,
    }
    const { error } = await supabase.from('feedback').insert(row)

    // Sentry is where a report actually gets seen. The table is the record;
    // this is the alarm. Sent even when the insert failed — a report that
    // cannot be stored is the one most worth knowing about — and never
    // allowed to take the form down with it.
    try {
      Sentry.captureFeedback(
        {
          name: user.email ?? user.id,
          email: user.email ?? undefined,
          message: `${row.subject}\n\n${row.body}`,
        },
        {
          captureContext: {
            level: kind === 'issue' ? 'error' : 'info',
            tags: {
              feedback_kind: kind,
              feedback_scope: row.scope,
              feedback_stored: error ? 'no' : 'yes',
            },
            extra: {
              path: row.path,
              church: church?.name ?? null,
              app_version: row.app_version,
              insert_error: error ? error.message : null,
            },
          },
        },
      )
    } catch { /* monitoring must never break the form */ }

    setBusy(false)
    if (error) return setErr(readableError(error))
    setSent(true)
  }

  if (sent) return (
    <Shell tabs>
      <div className="done-ic"><Check /></div>
      <h2 className="q center">{copy.done}</h2>
      <p className="sub center">{copy.doneSub}</p>
      <Sill />
      <button className="btn btn-quiet" onClick={() => nav('/account')}>Back to account</button>
    </Shell>
  )

  return (
    <Shell tabs back="/account" title="Account">
      <div className="eyebrow">{copy.eyebrow}</div>
      <h2 className="q">{copy.title}</h2>
      <p className="sub">{copy.sub}</p>
      <Sill />

      <Field label={copy.subjectLabel}>
        <input className="input" value={subject} maxLength={120} placeholder={copy.subjectHint}
               onChange={(e) => setSubject(e.target.value)} />
      </Field>
      {touched && missing === 'subject' && (
        <p className="err">A line here first — it is what we sort by.</p>
      )}

      <Field label={copy.bodyLabel}>
        <textarea className="ta" value={body} maxLength={4000} placeholder={copy.bodyHint}
                  rows={6} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {touched && missing === 'body' && (
        <p className="err">Tell us what happened, even roughly.</p>
      )}

      {canScope && (
        <div className="field">
          <label className="label">Who is this for?</label>
          <div className="segmented" role="group" aria-label="Scope">
            <button type="button" onClick={() => setScope('global')}
                    className={scope === 'global' ? 'is-on' : undefined}
                    aria-pressed={scope === 'global'}>Everyone</button>
            <button type="button" onClick={() => setScope('church')}
                    className={scope === 'church' ? 'is-on' : undefined}
                    aria-pressed={scope === 'church'}>{church.name}</button>
          </div>
          <p className="small" style={{ marginTop: 7 }}>
            {scope === 'global'
              ? 'A change to Beseter itself, for every writer on it.'
              : `Something that would help ${church.name} specifically.`}
          </p>
        </div>
      )}

      <button className="btn btn-primary" disabled={busy} onClick={send}>
        {busy ? 'Sending…' : copy.send}
      </button>

      {err && <p className="err">{err}</p>}

      <p className="small" style={{ marginTop: 16 }}>
        Sent with your name and the page you were on, so we can find it. Nothing
        from your songs goes with it.
      </p>

      {mine?.length > 0 && (
        <>
          <Sill />
          <div className="eyebrow">Earlier</div>
          <p className="sub">What you have sent us, and anything we sent back.</p>
          {mine.map((m) => (
            <div key={m.id} className="sup-thread">
              <div className="sup-head">
                <b>{m.subject}</b>
                <small>
                  {[m.kind === 'issue' ? 'Reported' : 'Suggested', ago(m.created_at)]
                    .filter(Boolean).join(' · ')}
                </small>
              </div>
              {m.reply_body ? (
                <div className="sup-reply">
                  <div className="sup-from">Our reply · {ago(m.replied_at)}</div>
                  {/* Sent as typed, shown as typed — the same reasoning as the
                      report itself on the staff side. */}
                  <p>{m.reply_body}</p>
                </div>
              ) : (
                <p className="sup-waiting">
                  {m.status === 'closed'
                    ? 'Closed. We did not write back on this one.'
                    : 'No reply yet. We read everything that comes in.'}
                </p>
              )}
            </div>
          ))}
        </>
      )}
    </Shell>
  )
}
